// src/components/resources/layouts/GeneratorGrowthCallout.tsx

'use client'

import Link from 'next/link'
import RiArrowRightLine from '~/icons/remix/generated/RiArrowRightLine'
import RiBarChartBoxLine from '~/icons/remix/generated/RiBarChartBoxLine'
import RiCheckboxCircleLine from '~/icons/remix/generated/RiCheckboxCircleLine'
import { siteConfig } from '~/lib/config/site'
import { getGeneratorGrowthCopy } from '~/lib/resources/generator-growth-config'
import { trackGeneratorBridgeClick } from '~/components/resources/tools/generators/shared/trackGeneratorBridge'
import {
  GENERATOR_GROWTH_CARD_CLASS,
  GENERATOR_GROWTH_CTA_CLASS,
  GENERATOR_GROWTH_ICON_TILE_CLASS,
} from '~/components/resources/tools/generators/shared/generatorGrowthStyles'

/**
 * Dedicated SaaS intake card for professional-intent generators. Rendered
 * separately from companion cross-links so related tools stay discoverable.
 *
 * @param {Object} props - Component props.
 * @param {string} props.resourceSlug - Source generator slug for analytics.
 * @returns {JSX.Element} Upsell card.
 */
export function GeneratorGrowthCallout({ resourceSlug }: { resourceSlug: string }): JSX.Element {
  const copy = getGeneratorGrowthCopy(resourceSlug)
  const destination = siteConfig.baseLinks.requestAccess

  return (
    <aside className={`mt-10 max-w-3xl ${GENERATOR_GROWTH_CARD_CLASS}`} aria-label="AdSights platform upsell">
      <div className="flex items-center gap-3">
        <div className={GENERATOR_GROWTH_ICON_TILE_CLASS}>
          <RiBarChartBoxLine className="h-5 w-5" aria-hidden />
        </div>
        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
          After the mockup
        </p>
      </div>

      <h3 className="mt-4 text-lg font-semibold leading-snug text-gray-900 dark:text-white">{copy.title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">{copy.body}</p>

      <ul className="mt-4 space-y-2.5">
        {copy.bullets.map(bullet => (
          <li key={bullet} className="flex items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300">
            <RiCheckboxCircleLine
              className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-600 dark:text-indigo-400"
              aria-hidden
            />
            <span className="leading-snug">{bullet}</span>
          </li>
        ))}
      </ul>

      <Link
        href={destination}
        onClick={() => {
          trackGeneratorBridgeClick({
            resourceSlug,
            placement: 'page_upsell',
            targetType: 'request_access',
            destinationPath: destination,
          })
        }}
        className={`${GENERATOR_GROWTH_CTA_CLASS} mt-5`}
      >
        {copy.ctaLabel}
        <RiArrowRightLine className="h-4 w-4" aria-hidden />
      </Link>
    </aside>
  )
}
